/**
 * Organization hooks — Phase 3.6.B.
 *
 * Pairs with BE routes:
 *   GET   /api/organization
 *   PATCH /api/organization
 *   POST  /api/organization/logo            → records an uploaded logo (FU-D pattern)
 *
 * GET is auth-only — any tenant member gets a 200, which is what lets a
 * `manage-users` admin read the guest-seat meter and a plain member read
 * the org-wide Done-visibility default. Only PATCH gates on `manage-org`,
 * so never add an `enabled:` gate keyed on that permission: it would
 * blank those readouts for exactly the people who need them. The My
 * Organization panel still hides its own write affordances for
 * non-holders so nothing on it 403s on submit.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiClient } from '@/api/client'
import { DASHBOARD_QUERY_KEY } from '@/features/dashboard/use-dashboard'
import { ME_QUERY_KEY } from '@/features/me/use-me'
import { CLIENTS_QUERY_KEY } from './use-clients'

import type { Organization, OrganizationInput } from './types'

export const ORGANIZATION_QUERY_KEY = ['organization'] as const

export function useOrganization(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ORGANIZATION_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await apiClient<{ data: Organization }>('/api/organization')
        return res.data
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return null
        }
        throw err
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: options?.enabled ?? true,
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()
  return useMutation<Organization, Error, OrganizationInput>({
    mutationFn: async (input) => {
      const res = await apiClient<{ data: Organization }>('/api/organization', {
        method: 'PATCH',
        body: input,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
      // The Sidebar reads `tenant.logoUrl` / `tenant.name` / `tenant.plan`
      // from `/api/me`, not from `/api/organization`. Without this
      // invalidate, the brand row at the top of the rail stays stale
      // until a hard refresh.
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
      // MOTE-143 — the BE PATCH hook mirrors `tenants.displayName ?? name`
      // onto the internal client's `company_name` in the same tx. The
      // client picker on project create/edit + the project-settings
      // General readout source that name from `useClients()`; without
      // this invalidate they'd display the stale label until the
      // 30s staleTime expires.
      queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY })
      // The org-wide Done visibility default affects every board read on
      // every non-overriding project, plus the master dashboard — the
      // BE re-filters Done cards per-request, so refetch both surfaces.
      // `['board']` is the prefix of every per-project `['board', id]`
      // cache entry.
      queryClient.invalidateQueries({ queryKey: ['board'] })
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY })
    },
  })
}

/**
 * Persist the org's logo URL after upload. The upload flow lands the
 * file in `next-{stage}-public-assets/logos/{uuid}.{ext}` and returns
 * its absolute CDN URL; we PATCH `/api/organization` with that URL
 * verbatim. The BE cascades a `s3:DeleteObject` on the OLD logo when
 * the URL changes (mirror of the avatar-replace flow).
 */
export function useSetOrganizationLogo() {
  const queryClient = useQueryClient()
  return useMutation<Organization, Error, { logoUrl: string }>({
    mutationFn: async ({ logoUrl }) => {
      const res = await apiClient<{ data: Organization }>('/api/organization', {
        method: 'PATCH',
        body: { logoUrl },
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
      // Sidebar's whitelabel branch reads `tenant.logoUrl` from
      // `/api/me`. Without this invalidate the brand row keeps showing
      // the previous logo until a hard refresh.
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}
